import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { ProductTagDisplay } from '@product-detail/models/product-tag.model';
import { getContractPillType } from '@product-detail/utils/product-tags-util';
import { ChipTypeEnum } from '@app/shared-product/components/usf-chip/models/chip-type.enum';
import { TagProduct } from '@app/shared-product/components/usf-product-tag-list/models/tag-product.model';
import { ApiUpdatable } from '@shared/models/api-updatable';
import { OrderItem } from '@usf/order-types';
import {
  CRITICAL_ERRORS,
  ImportOrderValidationErrorTypeEnum,
  ImportOrderValidationSubstituteTypeEnum,
  MINOR_ERRORS,
} from '../../../pages/import-order-validation/constants/import-order-validation.constants';
import { FEATURES } from '@shared/constants/splitio-features';
import { QuantityUpdate } from '@shared/models/quantity-update';

@Component({
  selector: 'app-import-order-validation-product',
  templateUrl: './import-order-validation-product.component.html',
  styleUrls: ['./import-order-validation-product.component.scss'],
})
export class ImportOrderValidationProductComponent implements OnChanges {
  public hasCriticalError = false;
  public hasMinorError = false;
  public vendorDirectTag = {
    content: ProductTagDisplay.direct,
    type: ChipTypeEnum.image,
    clickable: false,
    iconConfig: {
      name: 'DIRECT',
      iconWidth: 4.5,
    },
    iconUrl: 'assets/icon/direct.svg',
  };
  public onMLTag = {
    content: ProductTagDisplay.onMasterList,
    type: ChipTypeEnum.default,
    clickable: false,
    iconUrl: 'assets/icon/direct.svg',
  };
  public contractTag: TagProduct;
  public SubstituteTypeEnum = ImportOrderValidationSubstituteTypeEnum;
  public ErrorTypeEnum = ImportOrderValidationErrorTypeEnum;

  @Input() orderItem: OrderItem;
  @Input() showSubstitute: boolean;
  @Input() orderHeaderErrorCode: string;
  @Input() orderHeaderErrorMsg: string;
  @Output() substituteEmitter = new EventEmitter<any>();
  @Output() removeEmitter = new EventEmitter<any>();
  @Output() quantityEmitter = new EventEmitter<QuantityUpdate>();
  @Output() replaceEmitter = new EventEmitter<any>();

  showViewSubsLink = FEATURES.split_global_import_order_split_card.name;
  platformEnum = PlatformEnum;

  constructor(readonly platformService: PlatformService) {}

  ngOnChanges(): void {
    this.setErrorType();
    this.setContractTag();
  }

  get unitsOrdered(): ApiUpdatable<number> {
    return {
      currentValue: this.orderItem?.unitsOrdered || 0,
      previousValue: 0,
      loading: false,
      error: null,
    } as ApiUpdatable<number>;
  }

  get eachesOrdered(): ApiUpdatable<number> {
    return {
      currentValue: this.orderItem?.eachesOrdered || 0,
      previousValue: 0,
      loading: false,
      error: null,
    } as ApiUpdatable<number>;
  }

  setContractTag(): void {
    const contract = this.orderItem?.contract;
    if (!this.contractTag && !!contract) {
      const pillType = getContractPillType(
        contract.iconDescription,
        contract.clientConcept,
      );
      if (!!pillType) {
        this.contractTag = {
          content: `${contract.iconDescription} - Contract`,
          type: ChipTypeEnum.image,
          clickable: false,
          iconConfig: {
            name: contract.iconDescription,
            iconWidth: pillType.size,
          },
          iconUrl: pillType.iconUrl,
        };
      }
    }
  }

  setErrorType(): void {
    this.hasCriticalError = false;
    this.hasMinorError = false;
    if (
      CRITICAL_ERRORS.includes(this.orderItem?.orderItemErrorCode) ||
      CRITICAL_ERRORS.includes(this.orderHeaderErrorCode)
    ) {
      this.hasCriticalError = true;
    } else if (
      MINOR_ERRORS.includes(this.orderItem?.orderItemErrorCode) ||
      MINOR_ERRORS.includes(this.orderHeaderErrorCode)
    ) {
      this.hasMinorError = true;
    }
  }

  remove(): void {
    this.removeEmitter.emit();
  }

  changeQuantityHandler(event: QuantityUpdate): void {
    this.quantityEmitter.emit(event);
  }

  getSubstitute(): void {
    this.substituteEmitter.emit();
  }

  acceptReplaceSubstitute(event: any): void {
    this.replaceEmitter.emit(event);
  }

  get substituteReplacementAvailability(): number {
    // -1 => No Substitute/Replacement Logic
    // 0 => Substitute/Replacement Available
    // 1 => Substitute/Replacement No Available
    if (
      this.orderItem.orderItemErrorCode ===
        this.ErrorTypeEnum.MINOR_ERROR_1007 ||
      this.orderItem.orderItemErrorCode ===
        this.ErrorTypeEnum.CRITICAL_ERROR_1003 ||
      this.orderItem.orderItemErrorCode ===
        this.ErrorTypeEnum.CRITICAL_ERROR_1011 ||
      this.orderItem.orderItemErrorCode ===
        this.ErrorTypeEnum.CRITICAL_ERROR_1012
    ) {
      if (!!this.orderItem.subOrReplacementProduct) {
        if (
          this.orderItem.subOrReplacementProduct.substituteType !==
          this.SubstituteTypeEnum.REPLACEMENT
        ) {
          return 0;
        } else if (
          this.orderItem.orderItemErrorCode ===
          ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_1003
        ) {
          //'Discontinued' is the only with type 'replacement'
          return 0;
        }
      }
      return 1;
    }
    return -1;
  }
}
